import i18n from "../../i18n";
import IdTypePoint from "../../interfaces/robot/rooms/id-type-point";
import Point, { getSvgCoordinatesFromRosPoint, getSvgCoordinatesFromSimplePoint, setPointInRoom } from "../../interfaces/robot/rooms/point";
import RoomData from "../../interfaces/robot/rooms/room-data";

export const CLEANING_ZONE_FILL = "lightgreen"; // base green (unselected)
export const CLEANING_ZONE_FILL_SELECTED = "green"; // selected
export const CLEANING_ZONE_FILL_HOVERED = CLEANING_ZONE_FILL_SELECTED;
export const CLEANING_ZONE_STROKE = "green";

export const TNR_ZONE_FILL = "lightblue"; // base blue (unselected)
export const TNR_ZONE_FILL_SELECTED = "#118FF3"; // selected
export const TNR_ZONE_FILL_HOVERED = TNR_ZONE_FILL_SELECTED;
export const TNR_ZONE_STROKE = "#118FF3";

export const RED_ZONE_FILL = "lightcoral"; // base (unselected)
export const RED_ZONE_FILL_SELECTED = "red"; // selected
export const RED_ZONE_FILL_HOVERED = RED_ZONE_FILL_SELECTED;
export const RED_ZONE_STROKE = "red";

// ── Colour helpers ────────────────────────────────────────────────────────────

/**
 * Returns the correct fill colour for a cleaning or teach-and-repeat zone polygon.
 * Hover is always the inverse of the current state:
 *   • unselected + hovered → selected colour
 *   • selected   + hovered → unselected (base) colour
 */
export function getZoneFill(isTnR: boolean, isSelected: boolean, isHovered: boolean): string {
  if (isTnR) {
    if (isHovered) return isSelected ? TNR_ZONE_FILL : TNR_ZONE_FILL_SELECTED;
    return isSelected ? TNR_ZONE_FILL_SELECTED : TNR_ZONE_FILL;
  }
  if (isHovered) return isSelected ? CLEANING_ZONE_FILL : CLEANING_ZONE_FILL_SELECTED;
  return isSelected ? CLEANING_ZONE_FILL_SELECTED : CLEANING_ZONE_FILL;
}

/**
 * Returns the correct fill colour for a red zone polygon.
 * Hover is always the inverse of the current state:
 *   • unselected + hovered → selected colour (red)
 *   • selected   + hovered → unselected (base) colour (lightcoral)
 */
export function getRedZoneFill(isSelected: boolean, isHovered: boolean): string {
  if (isHovered) return isSelected ? RED_ZONE_FILL : RED_ZONE_FILL_SELECTED;
  return isSelected ? RED_ZONE_FILL_SELECTED : RED_ZONE_FILL;
}

/** Returns the stroke colour for a zone polygon based on its type. */
export function getZoneStroke(isTnR: boolean): string {
  return isTnR ? TNR_ZONE_STROKE : CLEANING_ZONE_STROKE;
}

/**
 * Converts zone points (unsorted) to an array of SVG coordinates, preserving
 * index order so the result maps 1-to-1 with the sorted input (nulls kept).
 */
export const zonePointsToSvgCoords = (
  points: { orderIndex: number; point: { coordinateX: number; coordinateY: number } }[],
  roomData: RoomData,
): ({ x: number; y: number } | null)[] =>
  points
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((p) => getSvgCoordinatesFromSimplePoint({ x: p.point.coordinateX, y: p.point.coordinateY }, roomData));

/**
 * Converts an already-sorted SVG coordinate array to the `"x1,y1 x2,y2 …"` string
 * expected by `<polygon points>`, skipping any null entries.
 */
export const svgCoordsToString = (svgCoords: ({ x: number; y: number } | null)[]): string =>
  svgCoords
    .filter((p): p is { x: number; y: number } => p != null)
    .map((p) => `${p.x},${p.y}`)
    .join(" ");

/**
 * Convenience: sort zone points → SVG coords → `<polygon points>` string.
 */
export const zonePointsToSvgString = (
  points: { orderIndex: number; point: { coordinateX: number; coordinateY: number } }[],
  roomData: RoomData,
): string => svgCoordsToString(zonePointsToSvgCoords(points, roomData));

/**
 * Computes the mid-point between every consecutive pair of SVG coordinates,
 * wrapping around so the last point pairs with the first.
 * `insertAtIndex` is the index at which a new corner point would be inserted.
 */
export const computeSvgMidPoints = (
  svgPoints: ({ x: number; y: number } | null)[],
): { x: number; y: number; insertAtIndex: number }[] =>
  svgPoints.reduce<{ x: number; y: number; insertAtIndex: number }[]>((acc, p, i) => {
    const next = svgPoints[(i + 1) % svgPoints.length];
    if (p != null && next != null) {
      acc.push({ x: (p.x + next.x) / 2, y: (p.y + next.y) / 2, insertAtIndex: i + 1 });
    }
    return acc;
  }, []);

export const compareZoneNames = (a: string, b: string) => {
  const prefixCleaningZone = `${i18n.t("cleaning-zone")} `;
  const prefixRedZone = `${i18n.t("red-zone")} `;
  const prefixRuleZone = `${i18n.t("rule-zone")} `;

  if (a.startsWith(prefixCleaningZone) && b.startsWith(prefixCleaningZone)) {
    a = a.replace(prefixCleaningZone, "");
    b = b.replace(prefixCleaningZone, "");
  } else if (a.startsWith(prefixRedZone) && b.startsWith(prefixRedZone)) {
    a = a.replace(prefixRedZone, "");
    b = b.replace(prefixRedZone, "");
  } else if (a.startsWith(prefixRuleZone) && b.startsWith(prefixRuleZone)) {
    a = a.replace(prefixRuleZone, "");
    b = b.replace(prefixRuleZone, "");
  }

  const aNumber = parseInt(a);
  const bNumber = parseInt(b);
  if (aNumber && bNumber) {
    return aNumber - bNumber;
  }

  return a.toLocaleLowerCase() < b.toLocaleLowerCase() ? -1 : 1;
};

export const getAllowedZoneShift = (shift: Point, points: Point[], roomData: RoomData) => {
  let allowedShift = { coordinateX: shift.coordinateX, coordinateY: shift.coordinateY } as Point;

  points.forEach((point) => {
    const shiftedPoint = setPointInRoom(
      {
        coordinateX: shift.coordinateX + point.coordinateX,
        coordinateY: shift.coordinateY + point.coordinateY,
      },
      roomData,
    );
    if (roundCompareAGreaterB(shiftedPoint.coordinateX, shift.coordinateX + point.coordinateX)) {
      allowedShift.coordinateX = Math.max(shiftedPoint.coordinateX - point.coordinateX, allowedShift.coordinateX);
    } else if (roundCompareAGreaterB(shift.coordinateX + point.coordinateX, shiftedPoint.coordinateX)) {
      allowedShift.coordinateX = Math.min(shiftedPoint.coordinateX - point.coordinateX, allowedShift.coordinateX);
    }
    if (roundCompareAGreaterB(shiftedPoint.coordinateY, shift.coordinateY + point.coordinateY)) {
      allowedShift.coordinateY = Math.max(shiftedPoint.coordinateY - point.coordinateY, allowedShift.coordinateY);
    } else if (roundCompareAGreaterB(shift.coordinateY + point.coordinateY, shiftedPoint.coordinateY)) {
      allowedShift.coordinateY = Math.min(shiftedPoint.coordinateY - point.coordinateY, allowedShift.coordinateY);
    }
  });
  return allowedShift;
};

const roundCompareAGreaterB = (a: number, b: number) => {
  return Math.round(a * 100000) > Math.round(b * 100000);
};

export const getPolygonAreaString = (polygonPoints: Point[], multiplier: number) => {
  return (calculatePolygonArea(polygonPoints) * multiplier).toFixed(1);
};

export const calculatePolygonArea = (polygonPoints: Point[]) => {
  let area = 0;
  for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
    const xi = polygonPoints[i].coordinateX;
    const yi = polygonPoints[i].coordinateY;
    const xj = polygonPoints[j].coordinateX;
    const yj = polygonPoints[j].coordinateY;
    area += xj * yi - yj * xi;
  }

  return Math.abs(area / 2.0);
};

export const calculateAvailableSpace = (imageSrc: string, zones: IdTypePoint[][], roomData: RoomData) => {
  return new Promise<number>((resolve) => {
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = image.width;
      canvas.height = image.height;

      if (ctx) {
        ctx.drawImage(image, 0, 0);

        // Draw the zones as a mask on the mask canvas
        ctx.fillStyle = "black"; // Color to distinguish the zones
        ctx.strokeStyle = "black"; // Border color
        ctx.lineWidth = 1;
        zones
          .filter((z) => z.length > 2)
          .forEach((z) => {
            ctx.beginPath();
            ctx.moveTo(
              getSvgCoordinatesFromRosPoint(z[0], roomData)!.x,
              getSvgCoordinatesFromRosPoint(z[0], roomData)!.y,
            );
            for (let i = 1; i < z.length; i++) {
              ctx.lineTo(
                getSvgCoordinatesFromRosPoint(z[i], roomData)!.x,
                getSvgCoordinatesFromRosPoint(z[i], roomData)!.y,
              );
            }
            ctx.closePath();
            ctx.stroke();
            ctx.fill();
          });

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] === 254 && data[i + 1] === 254 && data[i + 2] === 254) {
            count++;
          }
        }

        resolve(count * roomData.mapInformation.resolution * roomData.mapInformation.resolution);
      }
    };
    image.onerror = () => {
      resolve(0);
    };
    if (imageSrc.startsWith("data:image")) {
      // already a full data URI
      image.src = imageSrc;
    } else if (/^[A-Za-z0-9+/=]+$/.test(imageSrc)) {
      // raw base64 string (no prefix)
      image.src = `data:image/png;base64,${imageSrc}`;
    } else {
      // assume it's a URL
      image.src = imageSrc;
    }
  });
};

export const getCursorPointFromEvent = (event: MouseEvent | TouchEvent) => {
  let cursorPoint: Point = { coordinateX: 0, coordinateY: 0 };
  if (event instanceof MouseEvent) {
    cursorPoint.coordinateX = event.clientX;
    cursorPoint.coordinateY = event.clientY;
  } else if (event instanceof TouchEvent) {
    cursorPoint.coordinateX = event.changedTouches[0].clientX;
    cursorPoint.coordinateY = event.changedTouches[0].clientY;
  }
  return cursorPoint;
};

export const getNewName = (namePrefix: string, existingNames: string[]) => {
  let newIdNumber = 1;
  while (existingNames.includes(`${namePrefix} ${newIdNumber}`)) {
    newIdNumber++;
  }

  return `${namePrefix} ${newIdNumber}`;
};